/**
 * Correspondance de SITE entre deux URI — la question que pose chaque
 * remplissage, chaque audit par origine et chaque « entrees pour ce site ».
 *
 * CDC § « Reconnaitre le maximum d'applications et de sites » : une
 * correspondance naive sur « le dernier point » se trompe. `a.co.uk` et
 * `b.co.uk` ne sont PAS le meme site, pas plus que `alice.github.io` et
 * `bob.github.io` : les traiter comme tels, c'est presenter les identifiants
 * de l'un a l'autre — la faute la plus grave du domaine. La frontiere d'un
 * site est donc son DOMAINE ENREGISTRABLE (eTLD+1), calcule sur la Public
 * Suffix List officielle embarquee (`public-suffix-data.ts`, section privee
 * comprise), avec l'algorithme publie sur https://publicsuffix.org/list/.
 *
 * Pur, sans E/S ni reseau : `URL` ne sert qu'a normaliser (minuscules,
 * punycode), jamais a joindre quoi que ce soit.
 */
import { PUBLIC_SUFFIX_ICANN_RULE_COUNT, PUBLIC_SUFFIX_RULES } from './public-suffix-data.js';

interface SuffixRules {
  readonly exact: ReadonlySet<string>;
  /** `*.ck` est range sous `ck` : tout label place devant `ck` est un suffixe. */
  readonly wildcard: ReadonlySet<string>;
  /** `!www.ck` est range sous `www.ck`. */
  readonly exception: ReadonlySet<string>;
}

let rules: SuffixRules | null = null;
let icannRules: SuffixRules | null = null;

function buildRules(list: readonly string[]): SuffixRules {
  const exact = new Set<string>();
  const wildcard = new Set<string>();
  const exception = new Set<string>();
  for (const rule of list) {
    if (rule.startsWith('!')) exception.add(rule.slice(1));
    else if (rule.startsWith('*.')) wildcard.add(rule.slice(2));
    else exact.add(rule);
  }
  return { exact, wildcard, exception };
}

function suffixRules(): SuffixRules {
  rules ??= buildRules(PUBLIC_SUFFIX_RULES.split('\n'));
  return rules;
}

/** Les seules regles ICANN : sans la section privee, `alice.github.io` est un sous-domaine de `github.io`. */
function icannSuffixRules(): SuffixRules {
  icannRules ??= buildRules(PUBLIC_SUFFIX_RULES.split('\n').slice(0, PUBLIC_SUFFIX_ICANN_RULE_COUNT));
  return icannRules;
}

/**
 * Nombre de labels du suffixe public. Une exception l'emporte sur tout ; sinon la regle la plus longue ; a defaut, la
 * regle implicite « * » (`null` : aucune regle de la liste ne s'applique).
 */
function suffixLength(labels: readonly string[], { exact, wildcard, exception }: SuffixRules): number | null {
  let length: number | null = null;
  for (let i = 0; i < labels.length; i += 1) {
    const candidate = labels.slice(i).join('.');
    if (exception.has(candidate)) return labels.length - i - 1;
    const candidateLength = labels.length - i;
    if (i > 0 && wildcard.has(candidate)) length = Math.max(length ?? 0, candidateLength + 1);
    if (exact.has(candidate)) length = Math.max(length ?? 0, candidateLength);
  }
  return length;
}

const IPV4 = /^\d{1,3}(?:\.\d{1,3}){3}$/u;

function isIpLiteral(host: string): boolean {
  return host.startsWith('[') || IPV4.test(host);
}

/** Minuscules + punycode, sans point final ; `null` si ce n'est pas un nom d'hote. */
function normalizeHostname(hostname: string): string | null {
  let host = hostname.trim().toLowerCase();
  if (host.endsWith('.')) host = host.slice(0, -1);
  if (host === '' || host.startsWith('.') || host.includes('..')) return null;
  if (/[^\x21-\x7e]/u.test(host)) {
    try {
      host = new URL(`http://${host}`).hostname;
    } catch {
      return null;
    }
  }
  return host;
}

/**
 * Domaine enregistrable (eTLD+1) d'un nom d'hote : `login.example.co.uk` →
 * `example.co.uk`. `null` pour un suffixe public lui-meme (`co.uk`,
 * `github.io`), une adresse IP ou un nom invalide — il n'y a alors aucun
 * « site » plus large que l'hote exact.
 */
export function registrableDomain(hostname: string): string | null {
  const host = normalizeHostname(hostname);
  if (host === null || isIpLiteral(host)) return null;
  const labels = host.split('.');
  const length = suffixLength(labels, suffixRules()) ?? 1;
  if (labels.length <= length) return null;
  return labels.slice(labels.length - length - 1).join('.');
}

/**
 * Domaine enregistrable selon les seules regles CONNUES de la liste, comme
 * `net::registry_controlled_domains::GetDomainAndRegistry` de Chromium
 * (`EXCLUDE_UNKNOWN_REGISTRIES`) : `null` sous une zone que la liste ignore
 * (`intranet.local`), la ou `registrableDomain` applique la regle implicite.
 * `privateRules: false` ecarte la section privee (`EXCLUDE_PRIVATE_REGISTRIES`) :
 * `alice.github.io` -> `github.io`.
 */
export function knownRegistrableDomain(hostname: string, privateRules: boolean): string | null {
  const host = normalizeHostname(hostname);
  if (host === null || isIpLiteral(host)) return null;
  const labels = host.split('.');
  const length = suffixLength(labels, privateRules ? suffixRules() : icannSuffixRules());
  if (length === null || labels.length <= length) return null;
  return labels.slice(labels.length - length - 1).join('.');
}

/**
 * Nom d'hote normalise d'une URI d'entree ou de page (`https://` suppose si
 * le schema manque). `null` hors web (`androidapp://`, `ssh://`…) : l'association
 * application ↔ site est une autre question, qui ne se tranche pas ici.
 */
export function siteHostname(uri: string): string | null {
  const trimmed = uri.trim();
  if (trimmed === '') return null;
  const hasScheme = /^[a-z][a-z0-9+.-]*:/iu.test(trimmed) && !/^[^:/]+:\d+(?:[/?#]|$)/u.test(trimmed);
  if (hasScheme && !/^https?:\/\//iu.test(trimmed)) return null;
  try {
    return normalizeHostname(new URL(hasScheme ? trimmed : `https://${trimmed}`).hostname);
  } catch {
    return null;
  }
}

/**
 * Deux URI designent-elles le meme site ? Oui si l'hote est identique (y
 * compris une IP ou `localhost`), ou si les deux hotes ont le MEME domaine
 * enregistrable. Jamais sur un suffixe public partage : `a.github.io` et
 * `b.github.io` restent deux sites.
 */
export function isSameSite(uriA: string, uriB: string): boolean {
  const a = siteHostname(uriA);
  const b = siteHostname(uriB);
  if (a === null || b === null) return false;
  if (a === b) return true;
  const site = registrableDomain(a);
  return site !== null && site === registrableDomain(b);
}
